import React from 'react';
import { Icon } from './SvgIcons';

const DIFFICULTY_LABEL = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };

const subMarks = (sq) => {
  if (sq.has_subquestions && Array.isArray(sq.subquestions) && sq.subquestions.length > 0) {
    return sq.subquestions.reduce((s, ssq) => s + (parseInt(ssq.marks, 10) || 0), 0);
  }
  return parseInt(sq.marks, 10) || 0;
};

const totalForQuestion = (q) => {
  if (q.has_subquestions && Array.isArray(q.subquestions)) {
    return q.subquestions.reduce((s, sq) => s + subMarks(sq), 0);
  }
  return parseInt(q.marks, 10) || 0;
};

const TemplatePreview = ({
  templateName,
  description,
  questions = [],
  difficultyWeightage = {},
  classWeightage,
}) => {
  const totalMarks = questions.reduce((s, q) => s + totalForQuestion(q), 0);
  const totalQuestions = questions.length;
  const totalSubs = questions.reduce(
    (s, q) => s + (q.has_subquestions && Array.isArray(q.subquestions) ? q.subquestions.length : 0),
    0,
  );

  const showDifficultyWeights =
    typeof difficultyWeightage?.easy === 'number'
    || typeof difficultyWeightage?.medium === 'number'
    || typeof difficultyWeightage?.hard === 'number';
  const classEntries = classWeightage ? Object.entries(classWeightage) : [];

  return (
    <div className="tpl-preview">
      <div className="tpl-preview-header">
        <h2 className="tpl-preview-title">
          {templateName?.trim() || 'Untitled Template'}
        </h2>
        {description && (
          <p className="tpl-preview-description">{description}</p>
        )}
        <div className="tpl-preview-summary">
          <span><strong>{totalQuestions}</strong> Questions</span>
          {totalSubs > 0 && <span><strong>{totalSubs}</strong> Sub-questions</span>}
          <span><strong>{totalMarks}</strong> Marks</span>
        </div>
      </div>

      {showDifficultyWeights && (
        <div className="tpl-preview-weightage">
          <div className="tpl-weight-card">
            <Icon name="DifficultyDot" level="easy" />
            <span>Easy</span>
            <strong>{difficultyWeightage.easy ?? 0}%</strong>
          </div>
          <div className="tpl-weight-card">
            <Icon name="DifficultyDot" level="medium" />
            <span>Medium</span>
            <strong>{difficultyWeightage.medium ?? 0}%</strong>
          </div>
          <div className="tpl-weight-card">
            <Icon name="DifficultyDot" level="hard" />
            <span>Hard</span>
            <strong>{difficultyWeightage.hard ?? 0}%</strong>
          </div>
        </div>
      )}

      {classEntries.length > 0 && (
        <div className="tpl-preview-weightage">
          {classEntries.map(([cls, pct]) => (
            <div key={cls} className="tpl-weight-card">
              <Icon name="GraduationCap" size={16} />
              <span>{cls}</span>
              <strong>{pct ?? 0}%</strong>
            </div>
          ))}
        </div>
      )}

      {questions.length === 0 ? (
        <div className="tpl-preview-empty">
          <Icon name="FileText" size={32} />
          <p style={{ marginTop: '0.75rem' }}>
            No questions added yet. Add questions in the Edit tab to see the preview.
          </p>
        </div>
      ) : (
        <ol className="tpl-preview-q-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {questions.map((q, qIndex) => {
            const marks = totalForQuestion(q);
            return (
              <li key={qIndex} className="tpl-preview-q">
                <div className="tpl-preview-q-head">
                  <span className="tpl-preview-q-num">{q.question_number || `Q${qIndex + 1}`}.</span>
                  <span className="tpl-preview-q-type">{(q.question_type || '').replace(/_/g, ' ')}</span>
                  {!q.has_subquestions && (
                    <>
                      <span className="tpl-preview-q-meta">
                        <Icon name="DifficultyDot" level={q.difficulty || 'medium'} />
                        {DIFFICULTY_LABEL[q.difficulty] || 'Medium'}
                      </span>
                      {q.class_level && (
                        <span className="tpl-preview-q-meta">
                          <Icon name="GraduationCap" />
                          {q.class_level}
                        </span>
                      )}
                    </>
                  )}
                  <span className="tpl-preview-q-marks">{marks} marks</span>
                </div>

                {q.has_subquestions && Array.isArray(q.subquestions) && q.subquestions.length > 0 && (
                  <div className="tpl-preview-sub">
                    {q.subquestions.map((sq, sqIndex) => {
                      const hasParts = sq.has_subquestions && Array.isArray(sq.subquestions) && sq.subquestions.length > 0;
                      return (
                        <div key={sqIndex}>
                          <div className="tpl-preview-sub-row">
                            <span className="tpl-preview-sub-num">{sq.sub_number || String.fromCharCode(97 + sqIndex)})</span>
                            <span className="tpl-preview-q-type">{(q.question_type || '').replace(/_/g, ' ')}</span>
                            {!hasParts && (
                              <>
                                <span className="tpl-preview-q-meta">
                                  <Icon name="DifficultyDot" level={sq.difficulty || 'medium'} />
                                  {DIFFICULTY_LABEL[sq.difficulty] || 'Medium'}
                                </span>
                                {sq.class_level && (
                                  <span className="tpl-preview-q-meta">
                                    <Icon name="GraduationCap" />
                                    {sq.class_level}
                                  </span>
                                )}
                              </>
                            )}
                            <span className="tpl-preview-q-marks">{subMarks(sq)} marks</span>
                          </div>

                          {hasParts && (
                            <div className="tpl-preview-sub" style={{ marginLeft: '1.25rem' }}>
                              {sq.subquestions.map((ssq, ssqIndex) => (
                                <div key={ssqIndex} className="tpl-preview-sub-row">
                                  <span className="tpl-preview-sub-num">{ssq.sub_number || ssqIndex + 1})</span>
                                  <span className="tpl-preview-q-type">{(q.question_type || '').replace(/_/g, ' ')}</span>
                                  <span className="tpl-preview-q-meta">
                                    <Icon name="DifficultyDot" level={ssq.difficulty || 'medium'} />
                                    {DIFFICULTY_LABEL[ssq.difficulty] || 'Medium'}
                                  </span>
                                  {ssq.class_level && (
                                    <span className="tpl-preview-q-meta">
                                      <Icon name="GraduationCap" />
                                      {ssq.class_level}
                                    </span>
                                  )}
                                  <span className="tpl-preview-q-marks">{parseInt(ssq.marks, 10) || 0} marks</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default TemplatePreview;
